// services/legalRepresentativeService.js
import apiClient from './api';

class LegalRepresentativeService {
  
  // Récupérer les représentants légaux d'un compte avec pagination et filtres
  async getAccountRepresentatives(accountId, params = {}) {
    try {
      const {
        pageNumber = 1,
        pageSize = 20,
        search = null,
        status = null,
        mandateType = null,
        sortBy = 'CreationDate',
        sortDescending = true
      } = params;

      const queryParams = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        sortBy,
        sortDescending: sortDescending.toString()
      });

      // Ajouter les paramètres optionnels
      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (mandateType) queryParams.append('mandateType', mandateType);

      const response = await apiClient.get(
        `/api/LegalRepresentatives/account/${accountId}/representatives?${queryParams}`
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching account representatives:', error);
      throw error;
    }
  }

  // Récupérer les comptes administrés par un représentant (pour le modal de détails)
  async getManagedAccountsByRepresentative(representativeAccountId, params = {}) {
    try {
      const {
        pageNumber = 1,
        pageSize = 10,
        search = '',
        mandateType = '',
        status = '',
        sortBy = 'StartDate',
        sortDescending = true
      } = params;

      // Construction des paramètres de requête
      const queryParams = new URLSearchParams({
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        sortBy,
        sortDescending: sortDescending.toString()
      });

      // Ajouter les paramètres optionnels
      if (search) queryParams.append('search', search);
      if (mandateType) queryParams.append('mandateType', mandateType);
      if (status) queryParams.append('status', status);

      // console.log('Fetching managed accounts for representative:', representativeAccountId);
      // console.log('Query params:', Object.fromEntries(queryParams));

      const response = await apiClient.get(
        `/api/LegalRepresentatives/representative/${representativeAccountId}/managed-accounts?${queryParams}`
      );

      // console.log('Managed accounts response:', response.data);

      // S'assurer que la réponse a la structure attendue
      return {
        success: true,
        data: response.data.data || response.data.items || response.data || [],
        pagination: response.data.pagination || {
          pageNumber: pageNumber,
          pageSize: pageSize,
          totalCount: response.data.totalCount || 0,
          totalPages: Math.ceil((response.data.totalCount || 0) / pageSize)
        }
      };

    } catch (error) {
      // console.error('Error fetching managed accounts:', error);
      
      // Retourner une structure d'erreur cohérente
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Erreur lors du chargement des comptes administrés',
        data: [],
        pagination: null
      };
    }
  }

  /**
   * Récupérer les représentants légaux d'un domaine avec pagination et filtres
   * @param {number} domainId - ID du domaine
   * @param {Object} params - Paramètres de pagination et filtres
   * @returns {Promise} Données des représentants avec pagination
   */
  async getDomainRepresentatives(domainId, params = {}) {
    try {
      const {
        page = 1,
        pageSize = 20,
        search = null,
        hasManagedAccounts = null,
        sortBy = 'ManagedAccountsCount',
        sortOrder = 'desc'
      } = params;

      // Construction des paramètres de requête
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortBy,
        sortOrder
      });

      // Ajouter les paramètres optionnels
      if (search) queryParams.append('search', search);
      if (hasManagedAccounts !== null) queryParams.append('hasManagedAccounts', hasManagedAccounts.toString());

      const response = await apiClient.get(
        `/api/LegalRepresentatives/domain/${domainId}/representatives?${queryParams}`
      );

      return response.data;
    } catch (error) {
      // console.error('Error fetching domain representatives:', error);
      throw error('Error fetching domain representatives:', error);
    }
  }

  // Méthode pour dissocier un mandataire
   /**
   * Dissocier un ou plusieurs mandataires d'un compte
   * @param {Object} params
   * @returns {Promise<Object>}
   */
   async removeRepresentative({ accountId, representativeId = null, options = {} }) {
    try {
      
      const payload = {
        accountId,
        representativeId,
        options: {
          newStatus: options.newStatus || 'INACTIVE',
          forceEmailUpdate: options.forceEmailUpdate || false,
          newEmail: options.newEmail || null,
          reason: options.reason || null
        }
      };
            
      const response = await apiClient.post('/api/LegalRepresentatives/remove', payload);
      
      return response.data;
    } catch (error) {
      
      let errorMessage = 'Erreur lors de la dissociation';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Problème de connexion';
      }
      
      throw new Error(errorMessage);
    }
  }
}

export default new LegalRepresentativeService();